import { useEffect, useRef, useState } from 'react';
import { useGlobalAudio } from '../../context/GlobalAudioContext';

/**
 * SpeakingLogo — cercle blanc avec le logo Cabaza animé (3 personnages + toit).
 * S'anime comme s'il parlait quand isSpeaking=true.
 *
 * - Fallback Phase 1 : boucle CSS bounce/pulse si Web Audio non dispo
 * - Phase 3 (actif par défaut) : lip-sync amplitude réelle via AnalyserNode
 */
const SpeakingLogo = ({ size = 64, isSpeaking = false }) => {
    const { subscribeAmplitude } = useGlobalAudio() || {};
    const [amp, setAmp] = useState(0);
    const lastAmpRef = useRef(0);

    useEffect(() => {
        if (!isSpeaking) {
            setAmp(0);
            return;
        }
        if (!subscribeAmplitude) return;
        const unsub = subscribeAmplitude((newAmp) => {
            // Smoothing entre frames pour éviter les saccades
            lastAmpRef.current = lastAmpRef.current * 0.5 + newAmp * 0.5;
            setAmp(lastAmpRef.current);
        });
        return unsub;
    }, [isSpeaking, subscribeAmplitude]);

    // Facteur d'amplitude (0..1). En fallback (pas d'analyser), on affiche la boucle CSS.
    const hasLipSync = isSpeaking && amp > 0;
    const scaleY = 1 + amp * 0.18;
    const translateY = -amp * 4;
    const glowIntensity = 0.25 + amp * 0.5;

    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: '50%',
                backgroundColor: '#fff',
                boxShadow: `0 4px ${12 + amp * 16}px rgba(139, 92, 246, ${glowIntensity})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: size * 0.15,
                position: 'relative',
                overflow: 'hidden',
                transition: 'box-shadow 0.1s linear'
            }}
            className={isSpeaking && !hasLipSync ? 'cabaza-speaking-logo speaking' : 'cabaza-speaking-logo'}
        >
            <style>{`
                .cabaza-speaking-logo.speaking .person {
                    animation: cabazaBounce 0.6s ease-in-out infinite;
                    transform-origin: center bottom;
                }
                .cabaza-speaking-logo.speaking .person-left   { animation-delay: 0ms; }
                .cabaza-speaking-logo.speaking .person-center { animation-delay: 100ms; }
                .cabaza-speaking-logo.speaking .person-right  { animation-delay: 200ms; }
                .cabaza-speaking-logo.speaking { animation: cabazaPulse 1.2s ease-in-out infinite; }
                @keyframes cabazaBounce {
                    0%, 100% { transform: translateY(0) scaleY(1); }
                    50%      { transform: translateY(-2px) scaleY(1.04); }
                }
                @keyframes cabazaPulse {
                    0%, 100% { box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25); }
                    50%      { box-shadow: 0 4px 20px rgba(139, 92, 246, 0.55), 0 0 0 4px rgba(236, 72, 153, 0.12); }
                }
            `}</style>
            <svg
                viewBox="0 0 160 150"
                width="100%"
                height="100%"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <linearGradient id="cabazaLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#8B5CF6" />
                        <stop offset="100%" stopColor="#EC4899" />
                    </linearGradient>
                </defs>

                <path
                    d="M10,55 L80,10 L150,55"
                    fill="none"
                    stroke="url(#cabazaLogoGrad)"
                    strokeWidth="14"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Personnages : transform piloté par l'amplitude audio si lip-sync actif */}
                <g className="person person-left"
                   style={hasLipSync ? { transform: `translateY(${translateY * 0.8}px) scaleY(${1 + (scaleY - 1) * 0.8})`, transformOrigin: 'center bottom', transition: 'transform 0.06s linear' } : undefined}>
                    <circle cx="45" cy="68" r="12" fill="url(#cabazaLogoGrad)" />
                    <path d="M28,115 Q28,90 45,90 Q62,90 62,115 Z" fill="url(#cabazaLogoGrad)" />
                </g>

                <g className="person person-center"
                   style={hasLipSync ? { transform: `translateY(${translateY * 1.2}px) scaleY(${scaleY})`, transformOrigin: 'center bottom', transition: 'transform 0.06s linear' } : undefined}>
                    <circle cx="80" cy="60" r="14" fill="url(#cabazaLogoGrad)" />
                    <path d="M60,115 Q60,85 80,85 Q100,85 100,115 Z" fill="url(#cabazaLogoGrad)" />
                </g>

                <g className="person person-right"
                   style={hasLipSync ? { transform: `translateY(${translateY * 0.9}px) scaleY(${1 + (scaleY - 1) * 0.9})`, transformOrigin: 'center bottom', transition: 'transform 0.06s linear' } : undefined}>
                    <circle cx="115" cy="68" r="12" fill="url(#cabazaLogoGrad)" />
                    <path d="M98,115 Q98,90 115,90 Q132,90 132,115 Z" fill="url(#cabazaLogoGrad)" />
                </g>
            </svg>
        </div>
    );
};

export default SpeakingLogo;
